import { useColorMode } from "@chakra-ui/react";
import { getGridClassName, TcmEncounter, TcmEncounterResponse } from "@metriport/shared-internal";
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry,
  RowClickedEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css"; // React Grid Logic
import "ag-grid-community/styles/ag-theme-quartz.css"; // Core CSS
import { AgGridReact } from "ag-grid-react";
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { formatDateTimeInLocalNoYear } from "../../../shared/date";
import "../grid.css";
import { GetTcmEncounterQueryParams } from "./filters";
import { LatestStatusCell } from "./latest-status-cell";
import { TextCellEditor } from "./text-cell-editor";

ModuleRegistry.registerModules([InfiniteRowModelModule]);

/** Maps row number to page URL */
type RowMapping = Record<string, string>;

export type TransitionsOfCareGridRef = {
  resetSearch: (filters: GetTcmEncounterQueryParams) => void;
  updatePage: (filters: GetTcmEncounterQueryParams) => void;
  api: GridApi | null;
};

export type Props = {
  getTcmEncounters: (
    pageUrl?: string | undefined,
    filters?: GetTcmEncounterQueryParams | undefined
  ) => Promise<TcmEncounterResponse | undefined>;
  isLoading: boolean;
  numItemsPerPage?: number | undefined;
  onRowClick?: (row: TcmEncounter) => void;
  setEventTypeFilter?: (eventType: "Admitted" | "Discharged" | "") => void;
  onNoteUpdate?: (encounterId: string, note: string) => void;
};

type GridState = {
  rowMapping: RowMapping;
  filters: GetTcmEncounterQueryParams;
};
const initialState: GridState = { rowMapping: {}, filters: {} };

const TransitionsOfCareGrid = forwardRef<TransitionsOfCareGridRef, React.PropsWithChildren<Props>>(
  (
    {
      getTcmEncounters,
      isLoading,
      numItemsPerPage = 5,
      onRowClick,
      setEventTypeFilter,
      onNoteUpdate,
    }: Props,
    ref: ForwardedRef<TransitionsOfCareGridRef>
  ) => {
    const { colorMode } = useColorMode();
    const [api, setApi] = useState<GridApi | null>(null);
    const [gridState] = useState<GridState>(initialState);

    const columnDefs: ColDef[] = useMemo(() => {
      return [
        { field: "id", hide: true },
        { field: "patientLastName", headerName: "Last Name", sortable: true },
        { field: "patientFirstName", headerName: "First Name", sortable: true },
        { field: "patientDateOfBirth", headerName: "DOB", sortable: false },
        { field: "facilityName", headerName: "Admitting Facility", sortable: false },
        {
          field: "class",
          headerName: "Class",
          sortable: false,
          cellStyle: (params: { value: string }) => {
            return params.value === "emergency" || params.value === "inpatient encounter"
              ? { backgroundColor: colorMode === "dark" ? "#ef93a1" : "#ffebee" }
              : { backgroundColor: colorMode };
          },
        },
        {
          field: "admitTime",
          headerName: "Admit Date",
          sortable: true,
          cellRenderer: (params: { value: Date | null | undefined }) =>
            formatDateTimeInLocalNoYear(params.value),
        },
        {
          field: "dischargeTime",
          headerName: "Discharge Date",
          sortable: true,
          cellRenderer: (params: { value: Date | null | undefined }) =>
            formatDateTimeInLocalNoYear(params.value),
        },
        {
          field: "latestStatus",
          headerName: "Latest Status",
          sortable: false,
          cellRenderer: LatestStatusCell,
        },
        {
          field: "freetextNote",
          headerName: "Notes",
          editable: true,
          cellRenderer: (params: { value: string | null | undefined }) => params.value ?? "",
          cellClass: "editable-cell",
          singleClickEdit: true,
          cellEditorPopup: true,
          cellEditor: TextCellEditor,
          cellEditorParams: { onNoteUpdate },
        },
      ];
    }, [colorMode]);

    const defaultColDef: ColDef = {
      flex: 1,
    };

    const getRows = useCallback(
      (api: GridApi) => async (params: IGetRowsParams) => {
        try {
          const { gridState: localGridState }: { gridState: GridState } = params.context;
          const currPageNumber = api.paginationGetCurrentPage();
          const currPageUrl = (localGridState.rowMapping ?? {})[currPageNumber];
          const filters = localGridState.filters;

          const admitTimeSort = api.getColumnState().find(c => c.colId === "admitTime")?.sort;
          const dischargeTimeSort = api
            .getColumnState()
            .find(c => c.colId === "dischargeTime")?.sort;
          const lastOutreachDateSort = api
            .getColumnState()
            .find(c => c.colId === "lastOutreachDate")?.sort;
          const patientFirstNameSort = api
            .getColumnState()
            .find(c => c.colId === "patientFirstName")?.sort;
          const patientLastNameSort = api
            .getColumnState()
            .find(c => c.colId === "patientLastName")?.sort;

          const resp = await getTcmEncounters(currPageUrl, {
            ...filters,
            ...(admitTimeSort ? { admitTimeSort } : {}),
            ...(dischargeTimeSort ? { dischargeTimeSort } : {}),
            ...(lastOutreachDateSort ? { lastOutreachDateSort } : {}),
            ...(patientFirstNameSort ? { patientFirstNameSort } : {}),
            ...(patientLastNameSort ? { patientLastNameSort } : {}),
          });
          if (!resp) {
            params.failCallback();
            return;
          }
          const { items: encounters, meta } = resp;
          if (!encounters) {
            params.failCallback();
            return;
          }

          const totalCountOfItems = getTotalCountOfItems(meta, currPageNumber, numItemsPerPage);
          params.successCallback(encounters, totalCountOfItems);
          updateRowMapping(meta, currPageNumber, localGridState);
        } catch (error) {
          params.failCallback();
        }
      },
      [getTcmEncounters, numItemsPerPage]
    );

    const onGridReady = useCallback(
      (params: GridReadyEvent) => {
        const dataSource: IDatasource = {
          rowCount: undefined,
          getRows: getRows(params.api),
        };
        params.api.setGridOption("datasource", dataSource);
        setApi(params.api);
      },
      [getRows]
    );

    useImperativeHandle(ref, () => ({
      resetSearch(filters: GetTcmEncounterQueryParams) {
        if (api) {
          gridState.filters = filters;
          gridState.rowMapping = {};
          api.paginationGoToPage(0);
          api.purgeInfiniteCache();
        }
      },
      updatePage(filters: GetTcmEncounterQueryParams) {
        if (api) {
          gridState.filters = filters;
          api.purgeInfiniteCache();
        }
      },
      api,
    }));

    const handleRowClick = useCallback(
      (event: RowClickedEvent) => {
        const clickedElement = event.event?.target as HTMLElement;
        const hasNoRowClick = clickedElement.closest(".editable-cell");

        if (hasNoRowClick) {
          return;
        }

        onRowClick?.(event.data);
      },
      [onRowClick]
    );

    return (
      <AgGridReact
        cacheBlockSize={numItemsPerPage}
        cacheOverflowSize={1}
        className={getGridClassName(colorMode)}
        columnDefs={columnDefs}
        context={{ gridState }}
        defaultColDef={defaultColDef}
        enableCellTextSelection={true}
        getRowId={params => params.data.id}
        headerHeight={60}
        infiniteInitialRowCount={0}
        loading={isLoading}
        maxBlocksInCache={1}
        maxConcurrentDatasourceRequests={1}
        onGridReady={onGridReady}
        onRowClicked={handleRowClick}
        // Hack to prevent null rows from showing when sorting by discharge time.
        onSortChanged={() => {
          if (!api || !setEventTypeFilter) return;

          const columnState = api.getColumnState();
          const dischargeTimeSort = columnState.find(c => c.colId === "dischargeTime")?.sort;

          if (dischargeTimeSort) {
            gridState.filters.type = "Discharged";
            setEventTypeFilter("Discharged");
          } else {
            gridState.filters.type = "";
            setEventTypeFilter("");
          }
        }}
        pagination={true}
        paginationPageSize={numItemsPerPage}
        paginationPageSizeSelector={false}
        rowClass="cursor-pointer"
        rowModelType={"infinite"}
        suppressCellFocus={true}
        suppressMultiSort={true}
      />
    );
  }
);

function updateRowMapping(
  paginationMeta: { nextPage?: string; prevPage?: string; itemsOnPage: number },
  currPageNumber: number,
  myState: GridState
): void {
  const nextPageNumber = currPageNumber + 1;
  const nextPageUrl = paginationMeta.nextPage;
  const rowMappingUpdate: RowMapping = {
    ...(nextPageNumber >= 0 && nextPageUrl ? { [nextPageNumber]: nextPageUrl } : {}),
  };
  if (currPageNumber === 1 && paginationMeta.prevPage) {
    rowMappingUpdate[0] = paginationMeta.prevPage;
  }
  const newRowMapping = { ...myState.rowMapping, ...rowMappingUpdate };
  myState.rowMapping = newRowMapping;
}

function getTotalCountOfItems(
  paginationMeta: { nextPage?: string; itemsOnPage: number },
  currPageNumber: number,
  numItemsPerPage: number
): number | undefined {
  if (!paginationMeta.nextPage) {
    const totalNumItems = paginationMeta.itemsOnPage + currPageNumber * numItemsPerPage;
    return totalNumItems;
  }
  return -1;
}

export default TransitionsOfCareGrid;
